import { logger } from '@moonpig/web-core-monitoring'
import { EppoExperimentOverrides } from '@moonpig/web-core-utils/src/eppoContext/EppoContext'
import { EppoClient, Flag, VariationType } from '@eppo/js-client-sdk-common'
import { DefaultSubjectAttributes } from '../../shared/DefaultSubjectAttributes'
import { setupGetAllExperimentVariants } from '../setupGetAllExperimentVariants/setupGetAllExperimentVariants'
import {
  GetExperiment,
  setupGetExperimentVariant,
} from '../setupGetExperimentVariant/setupGetExperimentVariant'

type SetupEppo = {
  sessionId: string
  customerId: string | null
  defaultAttributes: DefaultSubjectAttributes
  overrides: EppoExperimentOverrides
  client: EppoClient | null
}

export const setupEppo = async ({
  sessionId,
  customerId,
  defaultAttributes,
  overrides,
  client,
}: SetupEppo) => {
  const {
    getExperimentString,
    getExperimentBoolean,
    getExperimentJson,
    getExperimentInteger,
    getExperimentNumeric,
    trackedServerExperiments,
    canSendExperimentToServer,
  } = setupGetExperimentVariant({
    client,
    sessionId,
    customerId,
    defaultAttributes,
    overrides,
  })

  const getAllExperimentVariants = setupGetAllExperimentVariants({
    client,
    sessionId,
    customerId,
    defaultAttributes,
    overrides,
  })

  const getExperiment: GetExperiment = (experimentKey, args) => {
    const override = overrides[experimentKey]
    const clientConfiguration = client?.getFlagConfigurations()
    const config = clientConfiguration?.[experimentKey] as Flag | undefined
    const variationType = config?.variationType

    switch (variationType) {
      case VariationType.STRING:
        return getExperimentString(experimentKey, args)
      case VariationType.BOOLEAN:
        return getExperimentBoolean(experimentKey, args)
      case VariationType.JSON:
        return getExperimentJson(experimentKey, args)
      case VariationType.INTEGER:
        return getExperimentInteger(experimentKey, args)
      case VariationType.NUMERIC:
        return getExperimentNumeric(experimentKey, args)
      case undefined:
        // Handle overrides when experiment is not configured in Eppo
        // This matches useExperiment behavior for feature parity
        if (override) {
          return (override === 'false' ? '' : override) as typeof args.fallback
        }
        return args.fallback
      default:
        logger.fixToday(
          `Unknown variation type "${variationType}" for experiment "${experimentKey}"`,
        )
        return args.fallback
    }
  }

  const getFlag: GetExperiment = (flagKey, args) => {
    const nonLoggingArgs = {
      ...args,
      subjectAttributes: { ...args.subjectAttributes, shouldLog: false },
    }
    return getExperiment(flagKey, nonLoggingArgs)
  }

  return {
    getExperiment,
    getFlag,
    getAllExperimentVariants,
    trackedServerExperiments,
    canSendExperimentToServer,
  }
}
